import { FetchArgs } from '@reduxjs/toolkit/dist/query/react';

import { API_URL, END_POINTS } from '@config';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { getLocalStorageItem } from '@utils/storage';

import {
  getBranchRevenueMock,
  getGroupPerformanceMock,
  getPaymentLinksAndModesMock,
  getPaymentOverviewMock,
  getPaymentVolumeMock
} from './dashboard';
import {
  getAttendeeDetailMock,
  getEventAttendeeListMock,
  getEventDetailsMock,
  getEventFormDetailsMock,
  getEventListMock
} from './events';
import {
  approveExpenseMock,
  getExpenseCategoriesMock,
  getExpenseDetailMock,
  getExpenseListMock,
  getExpenseStatsMock,
  rejectExpenseMock
} from './expenses';
import {
  createFormMock,
  exportFormDropOffsMock,
  exportFormSubmissionsMock,
  getFormActivityLogMock,
  getFormDropOffsMock,
  getFormOverviewMock,
  getFormPresignedUrlMock,
  getFormShareLinksMock,
  getFormsListMock,
  getFormStatusCountsMock,
  getFormSubmissionDetailMock,
  getFormSubmissionsMock,
  getFormTemplatesMock,
  updateFormMock,
  updateFormSourcesMock,
  updateFormStatusMock
} from './forms';
import {
  createApiKeyMock,
  createWebhookMock,
  deleteApiKeyMock,
  deleteWebhookMock,
  getAllApiKeysMock,
  getAllSupportedEventsMock,
  getAllWebhooksMock,
  getProviderConfigMock,
  getProvidersMock,
  getSampleTemplatesMock,
  getTemplateConfigMock,
  getWebhookByIdMock,
  updateApiKeyMock,
  updateProviderMock,
  updateTemplateConfigMock,
  updateWebhookMock
} from './integrations';
import {
  getAllBranchesMock,
  getAppConfigMock,
  getBranchByIdMock,
  getCurrentSubscriptionMock,
  getDynamicComponentsMock,
  getOrganizationConfigMock,
  getOrganizationListMock,
  getRolesAndPermissionsMock,
  getUserDetailsMock
} from './layout';
import {
  getAgentsStatusSummaryMock,
  getGoogleStatusMock,
  getLeadActivitiesEventsMock,
  getLeadActivitiesTasksMock,
  getLeadActivitySubMock,
  getLeadAnalyticsConversionMock,
  getLeadCategoriesMock,
  getLeadCitiesMock,
  getLeadConfigMock,
  getLeadCustomFieldsMock,
  getLeadDashboardMock,
  getLeadDetailMock,
  getLeadIngestionAdsMock,
  getLeadIngestionProviderMock,
  getLeadInsightsMock,
  getLeadListMock,
  getLeadNotesMock,
  getLeadSourcesMock,
  getLeadsStuckSummaryMock,
  getLeadStatusMock,
  getLeadTagsMock,
  getLeadTimelineMock,
  getLeadVoiceNotesMock,
  getLostLeadSummaryMock,
  getSalesAgentsMock,
  getUserOrgSettingsMock
} from './leads';
import {
  addMembersToTrialGroupMock,
  addTemporaryTransaction,
  createMemberMock,
  getAccountDetailsMock,
  getBankAccountDetailByIdMock,
  getBankAccountListMock,
  getEsignDocumentsMock,
  getGroupDetailMock,
  getGroupMemberContactsMock,
  getGroupMembersMock,
  getGroupsListMock,
  getGroupsWithoutPaginationMock,
  getInvitedMembersMock,
  getMemberDetailMock,
  getMembersMock,
  getOrgMembersMock,
  getPaymentOrderHistoryMock,
  getTransactionDetailMock,
  getTransactionsMock
} from './pages';

const extractUrl = (args: string | FetchArgs): string => {
  if (typeof args === 'string') return args;
  return args.url;
};

const extractMethod = (args: string | FetchArgs): string => {
  if (typeof args === 'string') return 'GET';
  return (args.method || 'GET').toUpperCase();
};

const extractBody = (args: string | FetchArgs): any => {
  if (typeof args === 'string') return undefined;
  return args.body;
};

const parseQueryParams = (url: string): URLSearchParams => {
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return new URLSearchParams();
  return new URLSearchParams(url.slice(qIndex));
};

const ORG_CONFIG_URL_PATTERN = /\/organisation\/[^/]+\/config(?:\?|$)/;

export const isOrgConfigRequest = (args: string | FetchArgs): boolean =>
  ORG_CONFIG_URL_PATTERN.test(extractUrl(args));

const extractBranchId = (url: string): string | undefined => {
  const match = url.match(/\/branch\/([^/?]+)/);
  return match ? match[1] : undefined;
};

const parseDateRange = (params: URLSearchParams) => {
  const fromTs = params.get('from_timestamp') || '';
  const toTs = params.get('to_timestamp') || '';

  const toDateParts = (ts: string) => {
    if (!ts) {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch {
      return '01-01-2026';
    }
  };

  return {
    startDate: toDateParts(fromTs),
    endDate: toDateParts(toTs)
  };
};

type MockResponse = Record<string, unknown> | unknown[] | null;

const buildMockPatterns = (
  accountType?: string
): {
  pattern: RegExp;
  resolve: (
    url: string,
    params: URLSearchParams,
    method: string,
    body?: any
  ) => MockResponse | undefined;
}[] => [
  // ── Dashboard endpoints ──
  {
    pattern: /\/payment-summary/,
    resolve: (_url, params) => {
      const timeFilter = params.get('time_filter_type') || 'due_at';
      const { startDate, endDate } = parseDateRange(params);
      const branchId = params.get('branch_id') || undefined;
      return getPaymentOverviewMock(timeFilter, startDate, endDate, branchId);
    }
  },
  {
    pattern: /\/branch-revenue/,
    resolve: (_url, params) => {
      const { startDate, endDate } = parseDateRange(params);
      const branchId = params.get('branch_id') || undefined;
      return getBranchRevenueMock(
        startDate,
        endDate,
        branchId
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group-performance/,
    resolve: (_url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const timeFilter = params.get('time_filter_type') || 'due_at';
      const { startDate, endDate } = parseDateRange(params);
      const branchId = params.get('branch_id') || undefined;
      return getGroupPerformanceMock(
        page,
        timeFilter,
        startDate,
        endDate,
        branchId
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/payment-links-modes/,
    resolve: (_url, params) => {
      const timeFilter = params.get('time_filter_type') || 'due_at';
      const { startDate, endDate } = parseDateRange(params);
      const branchId = params.get('branch_id') || undefined;
      return getPaymentLinksAndModesMock(
        timeFilter,
        startDate,
        endDate,
        branchId
      );
    }
  },
  {
    pattern: /\/daily-payment-volume/,
    resolve: (_url, params) => {
      const branchId = params.get('branch_id') || undefined;
      return getPaymentVolumeMock(branchId) as unknown as MockResponse;
    }
  },

  // ── Layout / auth endpoints (order matters: specific before general) ──
  {
    pattern: /\/dynamic-components/,
    resolve: () => getDynamicComponentsMock() as unknown as MockResponse
  },
  {
    pattern: /\/role_permissions/,
    resolve: () => getRolesAndPermissionsMock() as unknown as MockResponse
  },
  {
    pattern: /\/organisation\/[^/]+\/subscription/,
    resolve: () => getCurrentSubscriptionMock() as unknown as MockResponse
  },
  {
    pattern: ORG_CONFIG_URL_PATTERN,
    resolve: () => getOrganizationConfigMock() as unknown as MockResponse
  },

  // ── Org Integrations settings (API Keys / Webhooks / WhatsApp) ──
  {
    pattern: /\/integrations\/trigger\/list/,
    resolve: () => getAllWebhooksMock() as unknown as MockResponse
  },
  {
    pattern: /\/integrations\/trigger\/supported-events/,
    resolve: () => getAllSupportedEventsMock() as unknown as MockResponse
  },
  {
    pattern: /\/integrations\/trigger(?:\?|$)/,
    resolve: (_url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      return createWebhookMock(body) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/integrations\/trigger\/([^/?]+)/,
    resolve: (url, _params, method, body) => {
      const match = url.match(/\/integrations\/trigger\/([^/?]+)/);
      const webhookId = match ? match[1] : '';
      if (method === 'GET') return getWebhookByIdMock(webhookId);
      if (method === 'PUT')
        return updateWebhookMock(webhookId, body) as unknown as MockResponse;
      if (method === 'DELETE') return deleteWebhookMock(webhookId);
      return undefined;
    }
  },
  {
    pattern: /\/integrations\/org-app\/list/,
    resolve: () => getAllApiKeysMock() as unknown as MockResponse
  },
  {
    pattern: /\/integrations\/org-app(?:\?|$)/,
    resolve: (_url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      return createApiKeyMock(body) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/integrations\/org-app\/([^/?]+)/,
    resolve: (url, _params, method, body) => {
      const match = url.match(/\/integrations\/org-app\/([^/?]+)/);
      const apiId = match ? match[1] : '';
      if (method === 'PUT') return updateApiKeyMock(apiId, body);
      if (method === 'DELETE') return deleteApiKeyMock(apiId);
      return undefined;
    }
  },
  {
    pattern: /\/notification-provider\/supported-providers/,
    resolve: () => getProvidersMock() as unknown as MockResponse
  },
  {
    pattern: /\/notification-provider\/supported-templates/,
    resolve: () => getSampleTemplatesMock() as unknown as MockResponse
  },
  {
    pattern: /\/notification-provider\/org-config/,
    resolve: (_url, _params, method, body) => {
      if (method === 'GET')
        return getProviderConfigMock() as unknown as MockResponse;
      if (method === 'POST')
        return updateProviderMock(body) as unknown as MockResponse;
      return undefined;
    }
  },
  {
    pattern: /\/notification-template\/config\/([^/?]+)/,
    resolve: (_url, _params, method) => {
      if (method !== 'GET') return undefined;
      return getTemplateConfigMock() as unknown as MockResponse;
    }
  },
  {
    pattern: /\/notification-template\/config(?:\?|$)/,
    resolve: (_url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      return updateTemplateConfigMock(body) as unknown as MockResponse;
    }
  },

  // ── Page-specific endpoints (before broad branch catch-all) ──
  {
    pattern: /\/branch\/[^/]+\/members(?:\?|$)/,
    resolve: (url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const search = params.get('search') || '';
      const groupId = params.get('group_id') || '';
      return getMembersMock(
        page,
        limit,
        search,
        groupId,
        extractBranchId(url)
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/organisation\/[^/]+\/members(?:\?|$)/,
    resolve: (_url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      return getOrgMembersMock(page, limit) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/invited_members(?:\?|$)/,
    resolve: () => getInvitedMembersMock() as unknown as MockResponse
  },
  {
    pattern: /\/payment-order\/mark-paid/,
    resolve: (_url, _params, method, body) => {
      if (method === 'POST') {
        return {
          payment_order_id: body?.payment_order_id || '',
          split_payment_order_id: null
        } as unknown as MockResponse;
      }
      return undefined;
    }
  },
  {
    pattern: /\/payment-order\/remind/,
    resolve: (_url, _params, method) => {
      if (method !== 'POST') return undefined;
      return {
        message: 'Reminder sent successfully'
      } as unknown as MockResponse;
    }
  },
  {
    pattern: /\/payment-order\/([^/?]+)\/details/,
    resolve: (url) => {
      const match = url.match(/\/payment-order\/([^/?]+)\/details/);
      const orderId = match ? match[1] : '';
      return getTransactionDetailMock(orderId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/payment-order\/instant-link/,
    resolve: (_url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      const memberDetails = (body?.member_details || []) as {
        name: string;
        mobile: string;
        country_code: string;
        quick_collect_amount?: string;
        quick_collect_note?: string;
      }[];
      const created = memberDetails.map((member) =>
        addTemporaryTransaction({
          name: member.name,
          mobile: member.mobile,
          countryCode: member.country_code,
          amount: member.quick_collect_amount || String(body?.amount || '0'),
          note: member.quick_collect_note || body?.note,
          groupId: body?.group_id
        })
      );
      return { payment_order_ids: created.map((t) => t.id) } as MockResponse;
    }
  },
  {
    pattern: /\/payment-order\/([^/?]+)(?:\?|$)/,
    resolve: (url, _params, method) => {
      if (method !== 'GET') return undefined;
      const match = url.match(/\/payment-order\/([^/?]+)/);
      const orderId = match ? match[1] : '';
      if (orderId === 'mark-paid') return undefined;
      return getPaymentOrderHistoryMock(orderId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/payment-order(?:\?|$)/,
    resolve: (url, params) => {
      const status = params.get('status') || 'all';
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const memberId = params.get('member_id') || undefined;
      return getTransactionsMock(
        status,
        page,
        limit,
        memberId,
        extractBranchId(url)
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/groups(?:\?|$)/,
    resolve: (url) =>
      getGroupsWithoutPaginationMock(
        extractBranchId(url)
      ) as unknown as MockResponse
  },
  {
    pattern: /\/group\/[^/]+\/members\/contact_numbers/,
    resolve: (url) => {
      const match = url.match(/\/group\/([^/?]+)/);
      const groupId = match ? match[1] : '';
      return getGroupMemberContactsMock(groupId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group\/[^/]+\/members(?:\?|$)/,
    resolve: (url, params) => {
      const match = url.match(/\/group\/([^/?]+)/);
      const groupId = match ? match[1] : '';
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const search = params.get('search') || '';
      return getGroupMembersMock(
        groupId,
        page,
        limit,
        search
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group\/[^/]+\/member\/([^/?]+)/,
    resolve: (url) => {
      const groupMatch = url.match(/\/group\/([^/?]+)/);
      const memberMatch = url.match(/\/member\/([^/?]+)/);
      const groupId = groupMatch ? groupMatch[1] : '';
      const memberId = memberMatch ? memberMatch[1] : '';
      return getMemberDetailMock(memberId, groupId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group\/([^/?]+)/,
    resolve: (url, _params, method, body) => {
      if (method !== 'PATCH') return undefined;
      const match = url.match(/\/group\/([^/?]+)/);
      const groupId = match ? match[1] : '';
      return addMembersToTrialGroupMock(
        groupId,
        body
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group\/([^/?]+)/,
    resolve: (url) => {
      const match = url.match(/\/group\/([^/?]+)/);
      const groupId = match ? match[1] : '';
      return getGroupDetailMock(groupId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/group(?:\?|$)/,
    resolve: (url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '12', 10);
      const status = params.get('status') || 'active';
      return getGroupsListMock(
        page,
        limit,
        status,
        extractBranchId(url)
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /v2\/organisation\/[^/]+\/account-detail(?:\?|$)/,
    resolve: () => getBankAccountListMock() as unknown as MockResponse
  },
  {
    pattern: /\/account-detail\/([^/?]+)/,
    resolve: (url) => {
      const match = url.match(/\/account-detail\/([^/?]+)/);
      const bankId = match ? match[1] : '';
      return getBankAccountDetailByIdMock(bankId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/e-sign\/documents/,
    resolve: () => getEsignDocumentsMock() as unknown as MockResponse
  },
  {
    pattern: /\/account-detail(?:\?|$)/,
    resolve: () => getAccountDetailsMock() as unknown as MockResponse
  },
  {
    pattern: /\/branch\/[^/]+\/member\/([^/?]+)/,
    resolve: (url) => {
      const match = url.match(/\/member\/([^/?]+)/);
      const memberId = match ? match[1] : '';
      return getMemberDetailMock(memberId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/branch\/[^/]+\/member(?:\?|$)/,
    resolve: (_url, _params, method) => {
      if (method === 'POST') {
        return createMemberMock() as unknown as MockResponse;
      }
      return undefined;
    }
  },

  // ── Forms endpoints (specific before general) ──
  {
    pattern: /\/form\/status-counts/,
    resolve: () => getFormStatusCountsMock() as unknown as MockResponse
  },
  {
    pattern: /\/form\/templates/,
    resolve: () => getFormTemplatesMock() as unknown as MockResponse
  },
  {
    pattern: /\/form\/([^/?]+)\/overview/,
    resolve: (url) => {
      const match = url.match(/\/form\/([^/?]+)\/overview/);
      const formId = match ? match[1] : '';
      return getFormOverviewMock(formId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/form\/([^/?]+)\/status\/([^/?]+)/,
    resolve: (url, _params, method) => {
      if (method !== 'PATCH') return undefined;
      const match = url.match(/\/form\/([^/?]+)\/status\/([^/?]+)/);
      const formId = match ? match[1] : '';
      const status = match ? match[2] : '';
      return updateFormStatusMock(formId, status) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/form\/[^/?]+\/submissions\/export/,
    resolve: () => exportFormSubmissionsMock() as unknown as MockResponse
  },
  {
    pattern: /\/form\/[^/?]+\/submissions\/([^/?]+)/,
    resolve: (url) => {
      const match = url.match(/\/submissions\/([^/?]+)/);
      const submissionId = match ? match[1] : '';
      return getFormSubmissionDetailMock(
        submissionId
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/form\/[^/?]+\/submissions(?:\?|$)/,
    resolve: (_url, params) =>
      getFormSubmissionsMock(params) as unknown as MockResponse
  },
  {
    pattern: /\/form\/[^/?]+\/drop-offs\/export/,
    resolve: () => exportFormDropOffsMock() as unknown as MockResponse
  },
  {
    pattern: /\/form\/[^/?]+\/drop-offs(?:\?|$)/,
    resolve: (_url, params) =>
      getFormDropOffsMock(params) as unknown as MockResponse
  },
  {
    pattern: /\/form\/[^/?]+\/activity-log/,
    resolve: (_url, params) =>
      getFormActivityLogMock(params) as unknown as MockResponse
  },
  {
    pattern: /\/form\/[^/?]+\/share-links/,
    resolve: (url) => {
      const match = url.match(/\/form\/([^/?]+)\/share-links/);
      const formId = match ? match[1] : '';
      return getFormShareLinksMock(formId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/form\/[^/?]+\/source/,
    resolve: (url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      const match = url.match(/\/form\/([^/?]+)\/source/);
      const formId = match ? match[1] : '';
      return updateFormSourcesMock(formId, body) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/form\/([^/?]+)(?:\?|$)/,
    resolve: (url, _params, method) => {
      if (method !== 'PUT') return undefined;
      const match = url.match(/\/form\/([^/?]+)(?:\?|$)/);
      const formId = match ? match[1] : '';
      return updateFormMock(formId) as unknown as MockResponse;
    }
  },
  {
    // Anchored to the branch segment (real URL: .../branch/:branchId/form) so
    // this doesn't swallow the unrelated event-form-details endpoint, whose
    // URL (v1/event/:eventId/form) also ends in a bare "/form".
    pattern: /\/branch\/[^/]+\/form(?:\?|$)/,
    resolve: (url, _params, method) => {
      if (method === 'POST') {
        return createFormMock() as unknown as MockResponse;
      }
      return getFormsListMock(url) as unknown as MockResponse;
    }
  },
  {
    pattern: /files\/presigned-url\/forms\//,
    resolve: () => getFormPresignedUrlMock() as unknown as MockResponse
  },

  // ── Expense Tracker endpoints ──
  {
    pattern: /\/expense\/tracker\/stats/,
    resolve: () => getExpenseStatsMock() as unknown as MockResponse
  },
  {
    pattern: /\/expense\/list/,
    resolve: (_url, params, _method, body) => {
      const offset = parseInt(params.get('offset') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const page = limit > 0 ? Math.floor(offset / limit) : 0;
      const status = body?.status || '';
      return getExpenseListMock(status, page, limit) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/expense\/category\/all/,
    resolve: () => getExpenseCategoriesMock() as unknown as MockResponse
  },
  {
    pattern: /\/expense\/[^/]+\/approve/,
    resolve: (url, _params, method) => {
      if (method !== 'POST') return undefined;
      const match = url.match(/\/expense\/([^/?]+)\/approve/);
      const expenseId = match ? match[1] : '';
      return approveExpenseMock(expenseId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/expense\/[^/]+\/reject/,
    resolve: (url, _params, method, body) => {
      if (method !== 'POST') return undefined;
      const match = url.match(/\/expense\/([^/?]+)\/reject/);
      const expenseId = match ? match[1] : '';
      return rejectExpenseMock(
        expenseId,
        body?.reason || ''
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/expense\/report\/download/,
    resolve: (_url, _params, method) => {
      if (method !== 'POST') return undefined;
      return { job_id: 'trial-expense-report-job' } as unknown as MockResponse;
    }
  },
  {
    pattern: /\/expense\/[^/]+(?:\?|$)/,
    resolve: (url, _params, method) => {
      if (method !== 'GET') return undefined;
      const match = url.match(/\/expense\/([^/?]+)/);
      const expenseId = match ? match[1] : '';
      return getExpenseDetailMock(expenseId) as unknown as MockResponse;
    }
  },

  // ── Report download job polling (shared by GST/custom/charges/expense reports) ──
  // Matches only the top-level `v1/job/{jobId}` status endpoint, not the
  // unrelated `v1/lms/organization/{id}/job/{jobId}` lead-ingestion job status.
  {
    pattern: /v1\/job\/[^/?]+/,
    resolve: (_url, _params, method) => {
      if (method !== 'GET') return undefined;
      return { status: 'completed', result: {} } as unknown as MockResponse;
    }
  },

  // ── Lead Management endpoints (specific before general) ──
  {
    pattern: /\/lms\/organization\/[^/]+\/dashboard\/agents/,
    resolve: (_url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const pageSize = parseInt(params.get('page_size') || '10', 10);
      return getAgentsStatusSummaryMock(
        page,
        pageSize
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/dashboard\/stuck-leads/,
    resolve: () => getLeadsStuckSummaryMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/dashboard\/lost-lead-summary/,
    resolve: () => getLostLeadSummaryMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/dashboard\/insights/,
    resolve: () => getLeadInsightsMock() as unknown as MockResponse
  },
  {
    pattern:
      /\/lms\/organization\/[^/]+\/dashboard\/lead-analytics\/conversion/,
    resolve: () => getLeadAnalyticsConversionMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/dashboard(?:\?|$)/,
    resolve: () => getLeadDashboardMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/activities\/tasks/,
    resolve: () => getLeadActivitiesTasksMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/activities\/events/,
    resolve: () => getLeadActivitiesEventsMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead-ingestion\/ad/,
    resolve: () => getLeadIngestionAdsMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead-ingestion\/provider/,
    resolve: () => getLeadIngestionProviderMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/cities/,
    resolve: () => getLeadCitiesMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+\/notes/,
    resolve: () => getLeadNotesMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+\/voice-notes/,
    resolve: () => getLeadVoiceNotesMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+\/timeline/,
    resolve: () => getLeadTimelineMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+\/activity/,
    resolve: () => getLeadActivitySubMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+\/custom-fields/,
    resolve: () => getLeadCustomFieldsMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead\/[^/?]+/,
    resolve: (url) => {
      const match = url.match(/\/lead\/([^/?]+)/);
      const leadId = match ? match[1] : '';
      return getLeadDetailMock(leadId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead(?:\?|$)/,
    resolve: (_url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const search = params.get('search') || '';
      return getLeadListMock(page, limit, search) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/category/,
    resolve: () => getLeadCategoriesMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/status/,
    resolve: () => getLeadStatusMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/tags/,
    resolve: () => getLeadTagsMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/lead-source(?:\?|$)/,
    resolve: () => getLeadSourcesMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/google/,
    resolve: () => getGoogleStatusMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/settings/,
    resolve: () => getUserOrgSettingsMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/report\/generate/,
    resolve: (_url, _params, method) => {
      if (method !== 'POST') return undefined;
      return { job_id: 'trial-lead-report-job' } as unknown as MockResponse;
    }
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/report\/[^/]+\/status/,
    resolve: () =>
      ({ status: 'success', signed_url: '' } as unknown as MockResponse)
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/config/,
    resolve: () => getLeadConfigMock() as unknown as MockResponse
  },
  {
    pattern: /\/lms\/organization\/[^/]+\/document/,
    resolve: () => ({ url: '' } as unknown as MockResponse)
  },
  {
    pattern: /\/lms\/organization/,
    resolve: () => ({} as unknown as MockResponse)
  },
  {
    pattern: /\/sales-agents/,
    resolve: () => getSalesAgentsMock() as unknown as MockResponse
  },

  // ── Event endpoints (specific before general) ──
  {
    pattern: /\/event\/[^/]+\/attendee\/[^/?]+/,
    resolve: (url) => {
      const match = url.match(/\/attendee\/([^/?]+)/);
      const attendeeId = match ? match[1] : '';
      return getAttendeeDetailMock(attendeeId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/event\/[^/]+\/attendee(?:\?|$)/,
    resolve: (url, params) => {
      const match = url.match(/\/event\/([^/]+)\/attendee/);
      const eventId = match ? match[1] : '';
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const search = params.get('search') || '';
      return getEventAttendeeListMock(
        eventId,
        page,
        limit,
        search
      ) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/event\/[^/]+\/overview/,
    resolve: (url) => {
      const match = url.match(/\/event\/([^/]+)\/overview/);
      const eventId = match ? match[1] : '';
      return getEventDetailsMock(eventId) as unknown as MockResponse;
    }
  },
  {
    pattern: /v1\/event\/[^/]+\/form/,
    resolve: (url) => {
      const match = url.match(/\/event\/([^/]+)\/form/);
      const eventId = match ? match[1] : '';
      return getEventFormDetailsMock(eventId) as unknown as MockResponse;
    }
  },
  {
    pattern: /\/branch\/[^/]+\/event(?:\?|$)/,
    resolve: (_url, params) => {
      const page = parseInt(params.get('page') || '0', 10);
      const limit = parseInt(params.get('limit') || '10', 10);
      const status = params.get('status') || '';
      const search = params.get('search') || '';
      return getEventListMock(
        page,
        limit,
        status,
        search
      ) as unknown as MockResponse;
    }
  },

  // ── Broad branch catch-all (must come AFTER specific sub-paths) ──
  {
    pattern: /\/organisation\/[^/]+\/branch\/[^/?]+/,
    resolve: (url) =>
      getBranchByIdMock(extractBranchId(url) || '') as unknown as MockResponse
  },
  {
    pattern: /\/organisation\/[^/]+\/branch(?:\?|$)/,
    resolve: () => getAllBranchesMock() as unknown as MockResponse
  },
  {
    pattern: /v1\/user\/[^/?]+/,
    resolve: () => getUserDetailsMock() as unknown as MockResponse
  },
  {
    pattern: /v1\/config(?:\?|$)/,
    resolve: () => getAppConfigMock() as unknown as MockResponse
  },
  {
    pattern: /v1\/organisation(?:\?|$)/,
    resolve: () =>
      getOrganizationListMock(accountType) as unknown as MockResponse
  }
];

export const resolveMockData = (
  args: string | FetchArgs,
  accountType?: string
): MockResponse | undefined => {
  const url = extractUrl(args);
  const method = extractMethod(args);
  const params = parseQueryParams(url);
  const body = extractBody(args);
  const mockPatterns = buildMockPatterns(accountType);

  for (let i = 0; i < mockPatterns.length; i += 1) {
    const { pattern, resolve } = mockPatterns[i];
    if (pattern.test(url)) {
      const result = resolve(url, params, method, body);
      if (result !== undefined) return result;
    }
  }

  return undefined;
};

// Config values (gstin, hsn_code, amounts, ...) stay fully mocked in demo mode,
// but these visibility flags gate whether whole UI sections render at all
// (e.g. Attendance tab). Pulling them from the real org keeps the demo
// experience matching what that org actually has enabled.
const ORG_CONFIG_VISIBILITY_FLAG_KEYS = [
  'is_alternate_pan_enabled',
  'is_amount_paid_till_date_enabled',
  'is_attendance_alert_via_sms_enabled',
  'is_attendance_alert_via_whatsapp_enabled',
  'is_attendance_feature_enabled',
  'is_branch_name_used_in_comms',
  'is_custom_notification_enabled',
  'is_email_feature_enabled',
  'is_event_platform_fee_enabled',
  'is_fee_configuration_enabled',
  'is_fine_feature_enabled',
  'is_gst_feature_enabled',
  'is_member_status_change_alert_enabled',
  'is_platform_fee_feature_enabled',
  'is_quick_collect_platform_fee_enabled',
  'is_schedule_reminder_feature_enabled',
  'is_standard_invoice_enabled'
] as const;

export const fetchRealOrgConfigVisibilityFlags = async (
  realOrgId: string
): Promise<Record<string, unknown> | null> => {
  try {
    const token = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    const response = await fetch(
      `${API_URL}/${END_POINTS.GROUP_MANAGEMENT.GET_ORGANIZATION_CONFIG(
        realOrgId
      )}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-org-id': realOrgId
        }
      }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return ORG_CONFIG_VISIBILITY_FLAG_KEYS.reduce<Record<string, unknown>>(
      (flags, key) => {
        if (key in data) flags[key] = data[key];
        return flags;
      },
      {}
    );
  } catch {
    return null;
  }
};

export const mergeRealOrgConfigVisibilityFlags = (
  mockResponse: MockResponse,
  realFlags: Record<string, unknown> | null
): MockResponse => {
  if (!realFlags || !mockResponse || Array.isArray(mockResponse)) {
    return mockResponse;
  }
  return { ...mockResponse, ...realFlags };
};

const TRIAL_PASSTHROUGH_PATTERNS: RegExp[] = [
  /\/payment-order\/demo\/send-link/
];

// The real org's feature-flag check (used to auto-exit trial mode if the
// real org loses access to demo mode) must always hit the real backend,
// even while isTrialMode is true — never served from the mock.
export const isTrialPassthrough = (
  args: string | FetchArgs,
  realOrgId?: string | null
): boolean => {
  const url = typeof args === 'string' ? args : args.url;
  if (realOrgId && new RegExp(`/organisation/${realOrgId}/feature`).test(url)) {
    return true;
  }
  return TRIAL_PASSTHROUGH_PATTERNS.some((p) => p.test(url));
};
